/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import { Area, BHK, Price } from "../../../../redux-magic/sub-interfaces/sub-interfaces";

/* Component Imports */

import { Typography } from "@mui/material";
import PrepareConfigString from "../../../../lib/PrepareConfigString";
import TimeAgo from "../../../../lib/TimeAgo";
import CardCTA from "./sub-components/CardCTA";
import PropertyCardCommission from "./sub-components/PropertyCardCommission";
import PropertyCardConfig from "./sub-components/PropertyCardConfig";
import PropertyCardImage from "./sub-components/PropertyCardImage";
import PropertyCardLocation from "./sub-components/PropertyCardLocation";
import PropertyCardPrice from "./sub-components/PropertyCardPrice";
import PropertyCardTitle from "./sub-components/PropertyCardTitle";
import PropertyCardType from "./sub-components/PropertyCardType";
import SenderDetails from "./sub-components/SenderDetails";

/* Styled Components */

const BroadcastCardContainer = styled("div", {
	shouldForwardProp: (prop) => prop !== "source" && prop !== "isEditor",
})<{ source: string; isEditor: boolean }>(({ theme, source, isEditor }) => ({
	background: theme.palette.background.paper,
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	borderRadius: "0.75rem",
	objectFit: "contain",
	width: "100%",
	gap: "0.75rem",
	scrollSnapAlign: "start",
	// flexShrink: 1,
	// flexGrow: 1,
	padding: "0rem 0rem 1rem 0rem",
	boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.05)",
	height: "auto",
}));

const PropertyCardContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	height: "100%",
	cursor: "pointer",
	padding: "0rem 1rem 0rem 1rem",
}));

const PropertyCardInfoContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	padding: "0rem 0rem 0rem 0.5rem",
	width: "100%",
	height: "100%",
	cursor: "pointer",
}));

const PriceAndCommissionContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	padding: "1rem 1rem 1rem 1rem",
	background: "#dbf1e8",
	width: "100%",
	gap: "0.75rem",
	cursor: "pointer",
	borderRadius: "0.75rem 0.75rem 0rem 0rem",
}));

const NotesContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.5rem",
	background: "#fff7f0",
	borderRadius: "0.75rem",
	padding: "1rem 1rem 1rem 1rem",
}));

const ShowMoreButton = styled(Typography)(({ theme }) => ({
	fontSize: "0.825rem",
	color: theme.palette.primary.main,
	cursor: "pointer",
	fontWeight: 500,
	marginTop: "0.25rem",
}));

const NotesValue = styled(Typography)(({ theme }) => ({
	fontSize: "0.825rem",
	lineHeight: "0.95rem",
	fontWeight: 400,
	color: theme.palette.text.primary,
}));

const NotesTitle = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	lineHeight: "0.75rem",
	fontWeight: 500,
	color: theme.palette.text.secondary,
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
}));

const SubTextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	margin: "0.5rem 0rem 0rem 0rem",
	width: "100%",
	cursor: "pointer",
	gap: "0.5rem",
	/* ipad Mini - 768 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		gap: "0.65rem",
	},
	/* 860 */
	[theme.breakpoints.up("sm860")]: {
		gap: "0.75rem",
	},
	/* 910 */
	[theme.breakpoints.up("sm910")]: {
		gap: "0.85rem",
	},
	/* 1190 ~~Desktop Layout Start~~ */
	[theme.breakpoints.up("md1190")]: {
		gap: "0.75rem",
	},
}));

const Timestamp = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	lineHeight: "0.75rem",
	fontWeight: 500,
	color: theme.palette.text.secondary,
}));

const PropertyCardBroadcast = ({
	_id,
	profile_context,
	session,
	dispatch,
	router,
	loading,
	property_slug,
	property_transaction_type,
	property_type,
	property_title,
	property_images,
	property_location,
	property_price,
	property_bhk,
	property_no_of_seats,
	property_area,
	created_by,
	created_at,
	source,
	isEditor,
	commission,
	notes,
	is_expired,
	invite_status,
	is_read,
	chat_id,
	property_id,
	is_property_deleted,
}: {
	_id: ObjectId | string;
	profile_context: any;
	session: StateInterface["redux_session"] | null;
	dispatch: Function;
	router: NextRouter;
	loading: boolean;
	property_slug: string;
	property_transaction_type: string;
	property_type: Array<string>;
	property_title: string;
	property_images: Array<any>;
	property_location: string;
	property_price: Price;
	property_bhk: BHK;
	property_no_of_seats: BHK;
	property_area: Area;
	created_by: StateInterface["broadcast"]["created_by"];
	created_at: string | Date;
	source?: string;
	isEditor?: boolean;
	commission: number;
	notes: string;
	is_expired: boolean;
	invite_status: string;
	is_read: boolean;
	chat_id: string | ObjectId;
	property_id: ObjectId | string;
	is_property_deleted: boolean;
}) => {
	/* Translations */

	const localLang = {
		notes: router.locale === "ar-AE" ? "ملاحظات" : router.locale === "kn-IN" ? "ಗಮನಿಸಿ" : "Notes",
	};

	const [isExpanded, setIsExpanded] = React.useState(false);

	/* The sender as the card shows them. A business sender is described by the business only
	   (19 Oct 2026): never its person's name, photo or phone, so a business that didn't load reads
	   blank with the default avatar. A personal sender keeps their own name, photo and masked phone. */

	const senderBusiness: any =
		created_by?.is_business_profile && typeof created_by?.business_profile_id === "object"
			? created_by?.business_profile_id
			: null;
	const senderUser: any =
		!created_by?.is_business_profile && typeof created_by?.user_id === "object" ? created_by?.user_id : null;

	const senderName: string = created_by?.is_business_profile
		? senderBusiness && "title" in senderBusiness
			? senderBusiness.title || ""
			: ""
		: senderUser && "first_name" in senderUser && senderUser.first_name
			? senderUser.first_name + (senderUser.last_name || "")
			: senderUser && "country_code" in senderUser && "phone" in senderUser
				? `${senderUser.country_code}****${(senderUser.phone as string).slice(-4)}`
				: "";

	const senderSlug: string | ObjectId = created_by?.is_business_profile
		? senderBusiness && "slug" in senderBusiness
			? senderBusiness.slug || ""
			: ""
		: senderUser && "_id" in senderUser && senderUser._id
			? senderUser._id
			: "";

	const senderDpUrl: string | ObjectId = created_by?.is_business_profile
		? senderBusiness && "display_picture_url" in senderBusiness && senderBusiness.display_picture_url?.file_id
			? senderBusiness.display_picture_url.file_id
			: "/images/icons/Avatar.svg"
		: senderUser && "profile_picture" in senderUser && senderUser.profile_picture
			? senderUser.profile_picture.file_id
			: "";

	const senderPremiumActive: boolean = created_by?.is_business_profile
		? Boolean(senderBusiness?.subscription_information?.active)
		: Boolean(senderUser?.subscription_information?.active);

	return (
		<React.Fragment>
			{/* Broadcast Card */}

			<BroadcastCardContainer
				source={source ? source : ""}
				isEditor={isEditor !== undefined ? isEditor : false}
			>
				<PriceAndCommissionContainer>
					{/* Price */}

					<PropertyCardPrice
						router={router}
						loading={loading}
						price={property_price}
					/>

					{/* Commission */}

					<PropertyCardCommission
						router={router}
						loading={loading}
						commission={commission}
						transaction_type={property_transaction_type}
					/>
				</PriceAndCommissionContainer>

				{/* Property Details */}

				<PropertyCardContentContainer
					onClick={() =>
						is_property_deleted
							? null
							: router.push(
									`${process.env.PRODUCTION_URL || ""}loading.html?callbackUrl=${encodeURIComponent(
										`${process.env.PRODUCTION_URL || ""}${router.locale}/properties/${property_slug}`,
									)}`,
								)
					}
				>
					{/* Image */}

					<PropertyCardImage
						images={property_images ? (property_images[0] ? property_images[0] : "") : ""}
						loading={loading}
						transaction_type={property_transaction_type}
					/>

					<PropertyCardInfoContainer
						sx={{
							padding: router.locale === "ar-AE" ? "0rem 0.5rem 0rem 0rem" : "0rem 0rem 0rem 0.5rem",
						}}
					>
						{/* Property Title */}

						<PropertyCardTitle
							loading={loading}
							title={
								is_property_deleted
									? "Deleted Property"
									: property_title !== "" || undefined
										? property_title
										: "Unknown Title"
							}
						/>

						<SubTextContainer sx={{ marginTop: loading ? "0rem" : "0.5rem" }}>
							{/* Property Location */}

							<PropertyCardLocation
								loading={loading}
								location={property_location !== "" || undefined ? property_location : "Unknown Location"}
							/>

							{/* Property Type */}

							<PropertyCardType
								loading={loading}
								propertyType={property_type}
							/>

							{/* Property Config */}

							<PropertyCardConfig
								loading={loading}
								config={PrepareConfigString(
									property_transaction_type,
									property_type,
									property_bhk,
									property_no_of_seats,
									property_area,
								)}
								transaction_type={property_transaction_type ? property_transaction_type : ""}
								property_type={property_type ? property_type : []}
							/>
						</SubTextContainer>
					</PropertyCardInfoContainer>
				</PropertyCardContentContainer>

				{/* Notes */}

				{notes && source === "broadcast_list" ? (
					<div
						style={{
							width: "100%",
							height: "100%",
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							alignItems: "center",
							padding: "0rem 1rem 0rem 1rem",
						}}
					>
						<NotesContainer>
							<NotesTitle>{localLang.notes}</NotesTitle>
							<NotesValue>
								{isExpanded ? notes : notes.slice(0, 240)}
								{notes.length > 240 && (
									<ShowMoreButton
										onClick={(e) => {
											e.stopPropagation();
											setIsExpanded(!isExpanded);
										}}
									>
										{isExpanded ? "Show less" : "Show more"}
									</ShowMoreButton>
								)}
							</NotesValue>
						</NotesContainer>
					</div>
				) : null}

				{source === "broadcast_list" ? (
					<RowContainer
						sx={{
							width: "100%",
							padding: "0rem 1rem 0rem 1rem",
						}}
					>
						{/* Sender Details */}

						<SenderDetails
							router={router}
							loading={loading}
							sender_is_business_profile={created_by?.is_business_profile}
							sender_name={senderName}
							sender_slug={senderSlug}
							sender_type={
								created_by?.is_business_profile &&
								typeof created_by?.business_profile_id === "object" &&
								"business_profile_type" in created_by?.business_profile_id
									? created_by?.business_profile_id.business_profile_type
									: "client"
							}
							sender_dp_url={senderDpUrl}
							sender_premium_active={senderPremiumActive}
						/>

						{/* Timestamp */}

						<Timestamp variant="body2">{TimeAgo(created_at ? new Date(created_at) : new Date())}</Timestamp>
					</RowContainer>
				) : null}

				{/* Actions */}

				{source === "broadcast_list" ? (
					<CardCTA
						session={session}
						dispatch={dispatch}
						router={router}
						profile_context={profile_context}
						loading={loading}
						invite_status={invite_status}
						broadcast_id={_id}
						broadcast_notes={notes}
						chat_dp={property_images ? (property_images[0] ? property_images[0] : "") : ""}
						sender_name={senderName}
						sender_is_business_profile={created_by?.is_business_profile}
						sender_user_id={typeof created_by?.user_id === "object" ? created_by?.user_id._id : created_by?.user_id}
						sender_business_profile_id={
							created_by?.business_profile_id &&
							typeof created_by.business_profile_id === "object" &&
							"_id" in created_by.business_profile_id
								? created_by.business_profile_id._id
								: (created_by?.business_profile_id as ObjectId | string)
						}
						property_id={property_id}
						property_name={property_title}
						// sender_dp={
						// 	created_by?.is_business_profile &&
						// 	typeof created_by?.business_profile_id === "object" &&
						// 	"display_picture_url" in created_by?.business_profile_id
						// 		? created_by?.business_profile_id.display_picture_url
						// 		: typeof created_by?.user_id === "object" &&
						// 			  "profile_picture" in created_by?.user_id &&
						// 			  created_by?.user_id.profile_picture
						// 			? created_by?.user_id.profile_picture
						// 			: {
						// 					file_id: "/images/icons/Avatar.svg",
						// 					exists: false,
						// 					original_file_server_path: "",
						// 				}
						// }
					/>
				) : null}
			</BroadcastCardContainer>
		</React.Fragment>
	);
};

export default PropertyCardBroadcast;
